import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSetAtom } from 'jotai'
import { useActiveChatId } from '@renderer/chat/store'
import { FloatingBarRegionSwap } from '../action-view/nested/floating-controls'
import { PendingAnswerableCard } from '../../components/pending-answerable-card'
import { answerableStepCount } from '../chat-output-view/chat-output'
import type { AnswerableProgress } from '@renderer/chat/integrations/answerable'
import { optimisticQuestionAnswersAtom } from '../chat-output-view/question-progress-state'
import { useConversationPendingAnswerables } from './use-conversation-pending-answerables'

/**
 * The Chat View's floating-bar slot: hoists this conversation's oldest pending
 * AskUserQuestion / AskUserFeedback above the chat input, exactly as the
 * output view's batch bar does. The thread's own card for the action defers
 * its pending answer to this card and settles as answers land (optimistic
 * progress first, `outputData` once submitted). Answering goes through the
 * staged-action `/answer` endpoints, so resolving here resolves the question
 * everywhere — toast, For You, actions panel — and fires its resume hook.
 */
export function ChatQuestionsFloatingBarSlot({ source = 'chat-view' }: { source?: 'chat-view' | 'chat-drawer' }) {
  const activeChatId = useActiveChatId()
  const pendingAnswerables = useConversationPendingAnswerables(activeChatId)
  const setOptimisticQuestionAnswers = useSetAtom(optimisticQuestionAnswersAtom)

  const pendingQuestions = useMemo(
    () =>
      pendingAnswerables.flatMap((action) => {
        const questionCount = answerableStepCount(action)
        return questionCount !== null ? [{ action, questionCount }] : []
      }),
    [pendingAnswerables],
  )

  // Combined step numbering across the conversation's pending questions,
  // stable as earlier ones resolve: remember each action's step count for as
  // long as the session lasts, so answering "Question 2 of 5" doesn't relabel
  // the next card "1 of 3". Same bookkeeping as the output view's batch bar.
  const questionSessionRef = useRef(new Map<string, number>())
  if (pendingQuestions.length === 0) {
    questionSessionRef.current.clear()
  } else {
    for (const { action, questionCount } of pendingQuestions) {
      if (!questionSessionRef.current.has(action.id)) {
        questionSessionRef.current.set(action.id, questionCount)
      }
    }
  }

  const pendingQuestion = pendingQuestions[0]
  const pendingQuestionId = pendingQuestion?.action.id
  const questionEntries = [...questionSessionRef.current.entries()]
  const questionOffset = pendingQuestion
    ? questionEntries
        .slice(
          0,
          questionEntries.findIndex(([actionId]) => actionId === pendingQuestion.action.id),
        )
        .reduce((total, [, count]) => total + count, 0)
    : 0
  const questionTotal = questionEntries.reduce((total, [, count]) => total + count, 0)

  // Only touch answers this card recorded: the Output View's bar shares the atom while its drawer is open.
  const recordedIdsRef = useRef(new Set<string>())
  const recordQuestionProgress = useCallback(
    (answer: AnswerableProgress) => {
      if (!pendingQuestionId) {
        return
      }
      recordedIdsRef.current.add(pendingQuestionId)
      setOptimisticQuestionAnswers((current) => ({ ...current, [pendingQuestionId]: answer }))
    },
    [pendingQuestionId, setOptimisticQuestionAnswers],
  )

  useEffect(() => {
    const pendingIds = new Set(pendingQuestions.map(({ action }) => action.id))
    const settledIds = [...recordedIdsRef.current].filter((actionId) => !pendingIds.has(actionId))
    if (settledIds.length === 0) return
    settledIds.forEach((actionId) => recordedIdsRef.current.delete(actionId))
    setOptimisticQuestionAnswers((current) => omitKeys(current, settledIds))
  }, [pendingQuestions, setOptimisticQuestionAnswers])

  // A drawer closes over a view that still owns the question (the Output View's rows read this progress).
  const clearsProgressOnUnmount = source === 'chat-view'
  useEffect(() => {
    if (!clearsProgressOnUnmount) return
    const recordedIds = recordedIdsRef.current
    return () => {
      setOptimisticQuestionAnswers((current) => omitKeys(current, [...recordedIds]))
    }
  }, [clearsProgressOnUnmount, setOptimisticQuestionAnswers])

  const progress = pendingQuestion
    ? {
        offset: questionOffset,
        total: questionTotal,
        hasFollowingSteps: questionOffset + pendingQuestion.questionCount < questionTotal,
      }
    : undefined

  return (
    <FloatingBarRegionSwap regionKey={pendingQuestion ? `question:${pendingQuestion.action.id}` : null} animateOnMount>
      {/* The answer mutation updates outputData optimistically, which removes
          this card immediately and settles the thread card behind it. */}
      {pendingQuestion && (
        <PendingAnswerableCard
          action={pendingQuestion.action}
          source={source}
          progress={progress}
          onProgress={recordQuestionProgress}
        />
      )}
    </FloatingBarRegionSwap>
  )
}

function omitKeys<T>(record: Record<string, T>, keys: Array<string>): Record<string, T> {
  if (!keys.some((key) => key in record)) return record
  return Object.fromEntries(Object.entries(record).filter(([key]) => !keys.includes(key)))
}
